import { useId, useState, type FormEvent } from "react";
import { MUTATIONS, useMutation } from "../../../api";
import type { WorkshopId } from "../../../api/workshop";
import { isEmail } from "../../../lib/validation-utils";
import Button from "../../../design-system/primitives/components/Button/Button";
import Dialog from "../../../design-system/primitives/components/Dialog/Dialog";
import TextInput from "../../../design-system/primitives/components/TextInput/TextInput";
import Text from "../../../design-system/primitives/components/Text/Text";
import styles from "./InviteTeachersButton.css";

type InviteTeachersButtonProps = {
  workshopId: WorkshopId;
};

/**
 * The "+ Einladen" action for the colleagues tab: opens a dialog asking for a
 * colleague's email address. A successful invite closes the dialog; the
 * colleague then shows up under the pending invites.
 */
const InviteTeachersButton = ({ workshopId }: InviteTeachersButtonProps) => {
  const errorId = useId();
  const formId = useId();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [doInviteTeacher, { isLoading }] = useMutation(MUTATIONS.inviteTeacher);

  const handleOpen = () => {
    setEmail("");
    setError(null);
    setOpen(true);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isEmail(email)) return;

    doInviteTeacher({ id: workshopId, email })
      ?.then(() => {
        setOpen(false);
      })
      .catch((mutationError: unknown) => {
        console.error("Inviting teacher failed", mutationError);
        setError(
          mutationError instanceof Error
            ? mutationError.message
            : "Die Einladung konnte nicht verschickt werden. Bitte versuche es erneut.",
        );
      });
  };

  return (
    <>
      <Button
        title="Einladen"
        iconLeft="plus"
        color="accent"
        size="large"
        onClick={handleOpen}
      />
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title="Kolleg*innen einladen"
        description="Gib die E-Mail-Adresse deiner Kolleg*in ein, wir schicken dann eine Einladung zur Lernreise."
        closeAriaLabel="Schließen"
        contentSlot={
          // The Dialog renders its footer outside this slot, so the submit
          // button joins the form via its `form` attribute rather than by nesting.
          <form id={formId} className={styles.form} onSubmit={handleSubmit}>
            <TextInput
              name="teacher-email"
              label="E-Mail-Adresse"
              inputMode="email"
              value={email}
              onChange={(event) => setEmail(event.target.value.trim())}
              error={Boolean(error)}
              aria-describedby={error ? errorId : undefined}
            />
            {error && (
              <Text id={errorId} variant="body-small" color="neutral-100">
                {error}
              </Text>
            )}
          </form>
        }
        footer={
          <Button
            title={isLoading ? "Wird eingeladen …" : "Einladen"}
            color="accent"
            size="large"
            type="submit"
            form={formId}
            disabled={isLoading || !isEmail(email)}
            fullWidth
          />
        }
      />
    </>
  );
};

export default InviteTeachersButton;
